import { useEffect, useRef, useState } from "preact/hooks";
import { api, type CredentialSummary, type FolderReport, type MailboxReport, type Me, type SearchHit } from "./api";
import { Link } from "./app";
import { formatAddress, formatAgo, formatBytes, formatCount, formatDate, formatDateTime } from "./format";

/** While indexing or syncing, poll often enough to show progress; once caught up, rarely. */
const POLL_BUSY_MS = 5000;
const POLL_IDLE_MS = 20000;

function pollInterval(status: MailboxReport | null): number {
  if (!status || status.state !== "active") return POLL_BUSY_MS;
  const busy = status.syncing || status.folders.some((f) => !f.backfillDone || f.newMailPending) || status.embedding.pendingChunks > 0;
  return busy ? POLL_BUSY_MS : POLL_IDLE_MS;
}
/** The design asks for a warning at 8 GB of the 10 GB Durable Object limit. */
export const STORAGE_WARN_BYTES = 8e9;

/** Polls /api/status every few seconds while the tab is visible. */
function useStatus(enabled: boolean) {
  const [status, setStatus] = useState<MailboxReport | null>(null);
  const [credentials, setCredentials] = useState<CredentialSummary | null>(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const tick = useRef(0);

  const latest = useRef<MailboxReport | null>(null);

  const refresh = async () => {
    try {
      const r = await api.status();
      latest.current = r.status;
      setStatus(r.status);
      setCredentials(r.credentials);
      setError("");
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout>;
    let stopped = false;
    const loop = async () => {
      if (document.visibilityState === "visible") await refresh();
      if (!stopped) timer = setTimeout(loop, pollInterval(latest.current));
    };
    void loop();
    const clock = setInterval(() => setNow(Date.now()), 1000);
    const onVisible = () => document.visibilityState === "visible" && void refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearInterval(clock);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, tick.current]);

  return { status, credentials, error, now, refresh };
}

export function Dashboard({ me, onCredentialsChanged }: { me: Me; onCredentialsChanged: () => void }) {
  const hasCredentials = me.credentials !== null;
  const { status, credentials, error, now, refresh } = useStatus(true);
  const creds = credentials ?? me.credentials;
  const needsPassword = creds?.needsPassword || status?.state === "needs_password";
  const [editingPassword, setEditingPassword] = useState(false);
  const wiping = status?.state === "wiping";

  const afterSave = () => {
    setEditingPassword(false);
    onCredentialsChanged();
    void refresh();
  };

  return (
    <main>
      {(!hasCredentials || needsPassword || editingPassword) && !wiping && (
        <ConnectCard
          me={me}
          needsPassword={Boolean(needsPassword)}
          replacing={editingPassword}
          onCancel={editingPassword ? () => setEditingPassword(false) : undefined}
          onSaved={afterSave}
        />
      )}
      {error && <p class="notice danger">Could not load status: {error}</p>}
      {status && (hasCredentials || wiping || status.folders.length > 0) && (
        <StatusCard
          status={status}
          credentials={creds}
          now={now}
          onChanged={refresh}
          onChangePassword={() => setEditingPassword(true)}
          onRemovedPassword={() => {
            onCredentialsChanged();
            void refresh();
          }}
        />
      )}
      {status && status.totals.indexed > 0 && !wiping && <SearchCard />}
      {status && hasCredentials && !wiping && status.folders.length > 0 && <ExclusionsCard status={status} onSaved={refresh} />}
      <ClientsCard />
      {status && (status.folders.length > 0 || hasCredentials) && !wiping && (
        <DeleteCard me={me} status={status} onDeleted={() => { onCredentialsChanged(); void refresh(); }} />
      )}
    </main>
  );
}

// ---------------------------------------------------------------- connect

function ConnectCard(props: { me: Me; needsPassword: boolean; replacing: boolean; onSaved: () => void; onCancel?: () => void }) {
  const { me } = props;
  const [password, setPassword] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.saveCredentials({ password, host: host || undefined, port: port || undefined });
      setPassword("");
      props.onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="card">
      <h2>{props.needsPassword ? "Re-enter your mail password" : props.replacing ? "Change mail password" : "Connect your mailbox"}</h2>
      {props.needsPassword ? (
        <p class="notice warn">
          The mail server rejected the saved password, so indexing stopped. It will not retry on its own, because repeated
          failures can get the server to block us. Enter the current password to resume.
        </p>
      ) : (
        <p class="sub">
          We sign in to <strong>{me.user.email}</strong> over IMAP and index your mail so ChatGPT and Claude can
          search it. The password is checked with a real login before it is saved, and stored encrypted.
        </p>
      )}
      <form onSubmit={submit} class="stack">
        <div class="row">
          <div class="field">
            <label for="pw">Mail password for {me.user.email}</label>
            <input
              id="pw"
              type="password"
              autocomplete="current-password"
              value={password}
              onInput={(e) => setPassword(e.currentTarget.value)}
              required
            />
          </div>
        </div>
        <details>
          <summary>Advanced: mail server</summary>
          <div class="row" style={{ marginTop: "10px" }}>
            <div class="field">
              <label for="host">IMAP host</label>
              <input id="host" type="text" placeholder={me.imapDefaults.host} value={host} onInput={(e) => setHost(e.currentTarget.value)} />
            </div>
            <div class="field" style={{ flex: "0 1 120px" }}>
              <label for="port">Port (TLS)</label>
              <input id="port" type="number" placeholder={String(me.imapDefaults.port)} value={port} onInput={(e) => setPort(e.currentTarget.value)} />
            </div>
          </div>
        </details>
        <div class="row">
          <button class="primary" type="submit" disabled={busy || !password}>
            {busy ? "Checking with the mail server…" : "Verify and save"}
          </button>
          {props.onCancel && (
            <button type="button" onClick={props.onCancel}>
              Cancel
            </button>
          )}
        </div>
        {error && <p class="error-text">{error}</p>}
      </form>
    </section>
  );
}

// ---------------------------------------------------------------- status

function stateBadge(s: MailboxReport): { label: string; tone: string; live: boolean } {
  switch (s.state) {
    case "active": {
      const backfilling = s.folders.some((f) => !f.backfillDone);
      const arriving = s.folders.some((f) => f.newMailPending);
      if (s.lastError) return { label: "Retrying after an error", tone: "warn", live: false };
      if (arriving) return { label: "New mail arriving", tone: "", live: true };
      if (backfilling) return { label: "Indexing history", tone: "", live: s.syncing };
      if (s.embedding.pendingChunks > 0) return { label: "Finishing up", tone: "", live: s.syncing };
      return { label: "Up to date", tone: "ok", live: false };
    }
    case "paused":
      return { label: "Paused", tone: "warn", live: false };
    case "needs_password":
      return { label: "Needs password", tone: "danger", live: false };
    case "wiping":
      return { label: "Deleting your data", tone: "danger", live: true };
    default:
      return { label: "Not connected", tone: "", live: false };
  }
}

function pct(f: FolderReport): number {
  if (f.serverMessages === null || f.serverMessages === 0) return f.backfillDone ? 100 : 0;
  return Math.min(100, (f.indexed / f.serverMessages) * 100);
}

function folderState(f: FolderReport): string {
  if (f.excluded) return "excluded by you";
  if (f.newMailPending) return "new mail arriving";
  if (!f.backfillDone && f.indexed === 0) return "waiting";
  if (!f.backfillDone) return "indexing history";
  return "complete";
}

function StatusCard(props: {
  status: MailboxReport;
  credentials: CredentialSummary | null;
  now: number;
  onChanged: () => void;
  onChangePassword: () => void;
  onRemovedPassword: () => void;
}) {
  const { status: s, now } = props;
  const badge = stateBadge(s);
  const [busy, setBusy] = useState("");
  const act = async (name: string, fn: () => Promise<unknown>) => {
    setBusy(name);
    try {
      await fn();
      props.onChanged();
    } finally {
      setBusy("");
    }
  };
  const folders = [...s.folders].sort((a, b) => (b.serverMessages ?? 0) - (a.serverMessages ?? 0));
  const newest = s.folders.map((f) => f.newest).filter(Boolean).sort().at(-1) ?? null;

  if (s.state === "wiping") {
    return (
      <section class="card stack">
        <div class="row spread">
          <h2>Deleting your data</h2>
          <span class="badge danger live">In progress</span>
        </div>
        <p class="muted">
          Your password is already gone. Removing {formatCount(s.wipe?.vectorsRemaining ?? 0)} search vectors, then the stored mail
          and the index. This page updates on its own.
        </p>
      </section>
    );
  }

  return (
    <section class="card">
      <div class="row spread">
        <div>
          <h2>Mailbox index</h2>
          <p class="sub" style={{ marginBottom: 0 }}>
            {s.email} · checked with the mail server {formatAgo(s.serverCheckedAt, now)}
          </p>
        </div>
        <span class={`badge ${badge.tone} ${badge.live ? "live" : ""}`}>{badge.label}</span>
      </div>

      <div style={{ marginTop: "16px" }}>
        <div class="row spread">
          <span class="big-number">{s.totals.percent.toFixed(s.totals.percent < 100 ? 1 : 0)}%</span>
          <span class="muted small">
            {formatCount(s.totals.indexed)} of {formatCount(s.totals.serverMessages)} messages indexed
          </span>
        </div>
        <div class="progress" style={{ marginTop: "6px" }} role="progressbar" aria-valuenow={s.totals.percent} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${s.totals.percent}%` }} />
        </div>
      </div>

      <div class="stats">
        <div class="stat">
          <div class="label">Newest indexed message</div>
          <div class="value">{formatDateTime(newest)}</div>
        </div>
        <div class="stat">
          <div class="label">Last sync</div>
          <div class="value">{formatAgo(s.lastSyncAt, now)}</div>
        </div>
        <div class="stat">
          <div class="label">Next sync</div>
          <div class="value">{s.syncing ? "running now" : s.nextSyncAt ? formatAgo(s.nextSyncAt, now) : "—"}</div>
        </div>
        <div class="stat">
          <div class="label">Search vectors</div>
          <div class="value">
            {formatCount(s.embedding.vectors)}
            {s.embedding.pendingChunks > 0 && <span class="muted small"> (+{formatCount(s.embedding.pendingChunks)} queued)</span>}
          </div>
        </div>
      </div>

      {s.lastError && (
        <p class="notice warn">
          Last error {formatAgo(s.lastErrorAt, now)}: {s.lastError}
        </p>
      )}
      {s.embedding.lastError && <p class="notice warn">Semantic indexing is behind: {s.embedding.lastError}. Keyword search still works.</p>}
      {s.quarantine.count > 0 && (
        <p class="notice warn">
          {s.quarantine.count === 1 ? "One message" : `${formatCount(s.quarantine.count)} messages`} could not be indexed after
          repeated tries and {s.quarantine.count === 1 ? "was" : "were"} skipped. Latest: {s.quarantine.last}
        </p>
      )}
      {s.footprint.databaseBytes > STORAGE_WARN_BYTES && (
        <p class="notice warn">
          The search index is using {formatBytes(s.footprint.databaseBytes)} of its 10 GB limit. Indexing stops at 9.5 GB; excluding large
          folders or bulk senders below makes room.
        </p>
      )}

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Folder</th>
              <th class="num">On server</th>
              <th class="num">Indexed</th>
              <th>Progress</th>
              <th class="hide-sm">Oldest indexed</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {folders.map((f) => (
              <tr key={f.name}>
                <td>
                  {f.label}
                  {f.hiddenByDefault && <span class="muted small"> · hidden from search</span>}
                </td>
                <td class="num">{formatCount(f.serverMessages)}</td>
                <td class="num">{formatCount(f.indexed)}</td>
                <td>
                  <div class="progress thin">
                    <span style={{ width: `${pct(f)}%` }} />
                  </div>
                </td>
                <td class="hide-sm">{formatDate(f.oldest)}</td>
                <td class="small">
                  {f.newMailPending ? <span class="badge live">{folderState(f)}</span> : <span class="muted">{folderState(f)}</span>}
                </td>
              </tr>
            ))}
            {folders.length === 0 && (
              <tr>
                <td colSpan={6} class="muted">
                  Waiting for the first sync…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div class="row" style={{ marginTop: "16px" }}>
        {s.state === "active" && (
          <button onClick={() => act("sync", api.syncNow)} disabled={busy !== ""}>
            {busy === "sync" ? "Starting…" : "Index now"}
          </button>
        )}
        {s.state === "active" && (
          <button onClick={() => act("pause", api.pause)} disabled={busy !== ""}>
            Pause indexing
          </button>
        )}
        {s.state === "paused" && (
          <button class="primary" onClick={() => act("resume", api.resume)} disabled={busy !== ""}>
            Resume indexing
          </button>
        )}
        {props.credentials && (
          <button onClick={props.onChangePassword} disabled={busy !== ""}>
            Change password
          </button>
        )}
        {props.credentials && (
          <button
            onClick={() =>
              confirm("Remove the saved mail password? Indexing stops; the existing index stays searchable.") &&
              act("remove", async () => {
                await api.removeCredentials();
                props.onRemovedPassword();
              })
            }
            disabled={busy !== ""}
          >
            Remove password
          </button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- search

function SearchCard() {
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [results, setResults] = useState<SearchHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (e: Event) => {
    e.preventDefault();
    if (!q.trim() && !unreadOnly) return;
    setBusy(true);
    setError("");
    try {
      const params: Record<string, string> = { q: q.trim() || "*" };
      if (folder) params.folder = folder;
      if (unreadOnly) params.unread = "true";
      setResults((await api.search(params)).results);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="card">
      <h2>Try a search</h2>
      <p class="sub">The same search ChatGPT and Claude use: by meaning and by exact words, across mail and attachments.</p>
      <form class="row" onSubmit={run}>
        <input
          type="search"
          style={{ flex: "1 1 260px" }}
          placeholder="e.g. invoice from Acme last spring"
          value={q}
          onInput={(e) => setQ(e.currentTarget.value)}
          aria-label="Search query"
        />
        <select value={folder} onChange={(e) => setFolder(e.currentTarget.value)} aria-label="Folder">
          <option value="">All except Junk and Trash</option>
          <option value="inbox">Inbox</option>
          <option value="sent">Sent</option>
          <option value="archive">Archive</option>
          <option value="junk">Junk</option>
          <option value="trash">Trash</option>
        </select>
        <label class="check">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.currentTarget.checked)} />
          Unread only
        </label>
        <button class="primary" type="submit" disabled={busy}>
          {busy ? "Searching…" : "Search"}
        </button>
      </form>
      {error && <p class="error-text">{error}</p>}
      {results && results.length === 0 && <p class="muted">No matches.</p>}
      {results && results.length > 0 && (
        <ul class="results">
          {results.map((r) => (
            <li key={r.id}>
              <Link href={`/m/${r.id}`} class="subject">
                {r.subject}
              </Link>
              {r.unread && <span class="badge" style={{ marginLeft: "8px" }}>unread</span>}
              {r.replied && <span class="badge ok" style={{ marginLeft: "8px" }}>replied</span>}
              <div class="meta">
                {formatAddress(r.from)} · {formatDate(r.date)} · {r.folders.join(", ")}
                {r.hasAttachments && " · attachments"} · matched by {r.matchedBy.join(" + ")}
              </div>
              <p class="snippet">
                {/* Keyword matches arrive wrapped in ** markers; show them highlighted. */}
                {r.snippet.split("**").map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : part))}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- exclusions

function ExclusionsCard({ status, onSaved }: { status: MailboxReport; onSaved: () => void }) {
  const [senders, setSenders] = useState(status.exclusions.senders.join("\n"));
  const [folders, setFolders] = useState<string[]>(status.exclusions.folders);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const toggle = (label: string) =>
    setFolders((cur) => (cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label]));

  const save = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const list = senders
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await api.setExclusions({ senders: list, folders });
      setSenders(r.exclusions.senders.join("\n"));
      setFolders(r.exclusions.folders);
      const parts = ["Saved."];
      if (r.removedMessages > 0) parts.push(`${r.removedMessages.toLocaleString()} messages removed from the index.`);
      if (r.rescanning) parts.push("Re-scanning to bring back mail that is no longer excluded.");
      setMessage(parts.join(" "));
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const skipped = status.exclusions.skippedMessages;
  return (
    <section class="card">
      <h2>Keep out of the index</h2>
      <p class="sub">
        Mail from or to these people, and these folders, is never downloaded, indexed or shown to ChatGPT and Claude. Adding an
        entry removes matching mail that is already indexed.{skipped > 0 && ` ${skipped.toLocaleString()} messages skipped so far.`}
      </p>
      <form class="stack" onSubmit={save}>
        <div>
          <label for="excl-senders">People and domains, one per line</label>
          <textarea
            id="excl-senders"
            rows={4}
            style={{ width: "100%" }}
            placeholder={"lawyer@lawfirm.com\n@hr-provider.com\npayroll-*@sembit.com"}
            value={senders}
            onInput={(e) => setSenders(e.currentTarget.value)}
          />
          <p class="muted small" style={{ margin: "4px 0 0" }}>
            An address, a domain like @example.com (which includes its subdomains), or a pattern with *. Matches From, To or Cc.
          </p>
        </div>
        <div>
          <label>Folders</label>
          <div class="row" style={{ gap: "6px 16px" }}>
            {status.folders.map((f) => (
              <label class="check" key={f.name}>
                <input type="checkbox" checked={folders.includes(f.label)} onChange={() => toggle(f.label)} />
                {f.label}
              </label>
            ))}
          </div>
        </div>
        <div class="row">
          <button class="primary" type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save exclusions"}
          </button>
          {message && <span class="muted small">{message}</span>}
        </div>
        {error && <p class="error-text">{error}</p>}
      </form>
    </section>
  );
}

// ---------------------------------------------------------------- clients

function ClientsCard() {
  const url = `${location.origin}/mcp`;
  const [copied, setCopied] = useState(false);
  return (
    <section class="card">
      <h2>Connect ChatGPT or Claude</h2>
      <p class="sub">Add this as a custom connector. You will be asked to approve it and to sign in with GitLab.</p>
      <div class="copy">
        <code>{url}</code>
        <button
          onClick={async () => {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <ul class="small muted" style={{ marginBottom: 0 }}>
        <li>Claude: Settings → Connectors → Add custom connector, paste the URL.</li>
        <li>ChatGPT: Settings → Apps &amp; Connectors → Create (developer mode), paste the URL, authentication OAuth.</li>
        <li>Claude Code: <code>claude mcp add --transport http sembit-email {url}</code></li>
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------- delete

function DeleteCard({ me, status, onDeleted }: { me: Me; status: MailboxReport; onDeleted: () => void }) {
  const [stage, setStage] = useState<"idle" | "typing" | "final">("idle");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const f = status.footprint;

  const run = async () => {
    setBusy(true);
    setError("");
    try {
      await api.deleteMyData(typed);
      setStage("idle");
      setTyped("");
      onDeleted();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="card danger-zone">
      <h2>Delete all my data</h2>
      <p class="sub">Removes your saved password, the search index, every search vector and the stored copies of your mail. Your mailbox itself is not touched.</p>
      <div class="stats">
        <div class="stat">
          <div class="label">Search index</div>
          <div class="value">{formatBytes(f.databaseBytes)}</div>
        </div>
        <div class="stat">
          <div class="label">Stored mail copies</div>
          <div class="value">{formatBytes(f.rawMailBytes)}</div>
        </div>
        <div class="stat">
          <div class="label">Search vectors</div>
          <div class="value">{formatCount(f.vectors)}</div>
        </div>
        <div class="stat">
          <div class="label">Messages</div>
          <div class="value">{formatCount(status.totals.indexed)}</div>
        </div>
      </div>
      {stage === "idle" && (
        <button class="danger" onClick={() => setStage("typing")}>
          Delete all my data…
        </button>
      )}
      {stage !== "idle" && (
        <div class="stack">
          <label for="confirm-email">Type {me.user.email} to confirm</label>
          <div class="row">
            <input
              id="confirm-email"
              type="text"
              style={{ flex: "1 1 260px" }}
              value={typed}
              autocomplete="off"
              onInput={(e) => setTyped(e.currentTarget.value)}
            />
            <button onClick={() => { setStage("idle"); setTyped(""); }}>Cancel</button>
          </div>
          {stage === "typing" && (
            <button class="danger" disabled={typed.trim().toLowerCase() !== me.user.email} onClick={() => setStage("final")}>
              Continue
            </button>
          )}
          {stage === "final" && (
            <div class="notice danger stack">
              <strong>This cannot be undone.</strong> Everything listed above is deleted now. Indexing again later starts from scratch.
              <div>
                <button class="danger solid" disabled={busy} onClick={run}>
                  {busy ? "Deleting…" : "Yes, delete everything now"}
                </button>
              </div>
            </div>
          )}
          {error && <p class="error-text">{error}</p>}
        </div>
      )}
    </section>
  );
}
